import { FileText, X } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { tasksService } from '../services/api';

const ACCEPT = '.png,.jpg,.jpeg,.pdf,.docx,.xlsx,.csv';

export function TaskAttachmentPicker({ files, onChange, disabled }) {
  const [error, setError] = useState('');
  return <div className="tp-attachment-picker">
    <div className="tp-attachment-picker-controls">
      <label className={`tp-attachment-select ${disabled ? 'tp-attachment-select-disabled' : ''}`}>
        Wybierz załącznik
        <input type="file" multiple accept={ACCEPT} disabled={disabled} onChange={event => {
          const next = [...files, ...Array.from(event.target.files || [])];
          event.target.value = '';
          if (next.length > 10) { setError('Możesz dodać maksymalnie 10 plików jednocześnie.'); return; }
          if (next.some(file => !file.size || file.size > 10 * 1024 * 1024)) {
            setError('Każdy załącznik musi mieć od 1 bajta do 10 MB.'); return;
          }
          if (next.some(file => !/\.(png|jpe?g|pdf|docx|xlsx|csv)$/i.test(file.name))) {
            setError('Wybierz pliki PNG, JPG, JPEG, PDF, DOCX, XLSX lub CSV.'); return;
          }
          setError(''); onChange(next);
        }} />
      </label>
      <span className="tp-attachment-help" tabIndex="0">
        Dopuszczalne pliki i rozmiar?
        <span className="tp-attachment-help-tooltip" role="tooltip">
          PNG, JPG, PDF, DOCX, XLSX lub CSV · maks. 10 MB
        </span>
      </span>
    </div>
    {error && <p className="tp-error" role="alert">{error}</p>}
    {!!files.length && <div className="tp-selected-attachments">
      {files.map((file, index) => <div className="tp-selected-attachment" key={`${file.name}-${file.size}-${index}`}>
        <FileText size={20} aria-hidden="true" />
        <span><strong title={file.name}>{file.name}</strong><small>{(file.size / 1024).toFixed(1)} KB</small></span>
        <button type="button" disabled={disabled} aria-label={`Usuń wybrany plik ${file.name}`}
          onClick={() => { setError(''); onChange(files.filter((_, i) => i !== index)); }}><X size={17} /></button>
      </div>)}
    </div>}
  </div>;
}

function Attachment({ organizationId, taskId, attachment }) {
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [previewRequested, setPreviewRequested] = useState(false);
  const closePreview = useCallback(() => {
    setPreview(null);
    setPreviewRequested(false);
  }, []);

  useEffect(() => {
    if (!preview) return undefined;
    const previousOverflow = document.body.style.overflow;
    const closeOnEscape = event => {
      if (event.key === 'Escape') closePreview();
    };
    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [preview, closePreview]);

  useEffect(() => {
    if (!previewRequested) return undefined;
    let active = true;
    let url;
    setBusy(true); setError('');
    tasksService.attachment(organizationId, taskId, attachment.id, true).then(blob => {
      if (!active) return;
      url = URL.createObjectURL(new Blob([blob], { type: attachment.media_type }));
      setPreview(url);
    }).catch(() => {
      if (active) {
        setError('Nie udało się wczytać podglądu.');
        setPreviewRequested(false);
      }
    })
      .finally(() => { if (active) setBusy(false); });
    return () => { active = false; if (url) URL.revokeObjectURL(url); };
  }, [previewRequested, organizationId, taskId, attachment.id, attachment.media_type]);
  const download = async () => {
    setBusy(true); setError('');
    try {
      const blob = await tasksService.attachment(organizationId, taskId, attachment.id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = attachment.filename;
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (_) { setError('Nie udało się pobrać załącznika.'); }
    finally { setBusy(false); }
  };
  return <div className="tp-attachment">
    <span>{attachment.filename} ({(attachment.size / 1024).toFixed(1)} KB)</span>
    <div className="tp-attachment-actions">
      <button type="button" disabled={busy} onClick={download}>Pobierz</button>
      {['image/png', 'image/jpeg'].includes(attachment.media_type) && <button type="button" disabled={busy}
        onClick={() => setPreviewRequested(true)}>Podgląd</button>}
    </div>
    {busy && <small role="status">Wczytywanie…</small>}
    {error && <p role="alert" className="tp-error">{error}</p>}
    {preview && createPortal(
      <div className="tp-attachment-lightbox" role="presentation" onMouseDown={closePreview}>
        <section className="tp-attachment-lightbox-dialog" role="dialog" aria-modal="true"
          aria-label={`Podgląd załącznika ${attachment.filename}`} onMouseDown={event => event.stopPropagation()}>
          <header>
            <strong>{attachment.filename}</strong>
            <button type="button" autoFocus onClick={closePreview} aria-label="Zamknij podgląd"><X size={24} /></button>
          </header>
          <div className="tp-attachment-lightbox-content">
            <img className="tp-attachment-preview" src={preview} alt={attachment.filename} />
          </div>
        </section>
      </div>,
      document.body,
    )}
  </div>;
}

export function TaskAttachmentList({ attachments = [], organizationId, taskId }) {
  return <div className="tp-attachments">{attachments.map(attachment => <Attachment key={attachment.id}
    organizationId={organizationId} taskId={taskId} attachment={attachment} />)}</div>;
}
